import { useMemo, useState } from "react";
import { useStore, type ConversationSummary, type FolderSummary, type HomeTopic } from "@/lib/store";
import type { ModelInfo } from "@/lib/api";

const RECENT_DAYS = 7;
const RECENT_LIMIT = 7;

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

function formatContextLength(n: number | null | undefined): string {
  if (!n) return "—";
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  return String(n);
}

type Props = {
  conversations: ConversationSummary[];
  folders: FolderSummary[];
  activeModel: string;
  models: ModelInfo[];
  onPickTopic: (topic: HomeTopic) => void;
  onSelectChat: (id: string) => void;
};

type TopicEditor = { mode: "create" } | { mode: "edit"; index: number; original: HomeTopic };

export function HomeIntro({ conversations, activeModel, models, onPickTopic, onSelectChat }: Props) {
  const topics = useStore((s) => s.settings.homeTopics);
  const updateSettings = useStore((s) => s.updateSettings);
  const skills = useStore((s) => s.skills);
  const [editor, setEditor] = useState<TopicEditor | null>(null);
  const [draftLabel, setDraftLabel] = useState("");
  const [draftPrompt, setDraftPrompt] = useState("");
  const [draftSkillIds, setDraftSkillIds] = useState<string[]>([]);

  const recents = useMemo(() => {
    const cutoff = Date.now() - RECENT_DAYS * 24 * 60 * 60 * 1000;
    return [...conversations]
      .filter((c) => c.updatedAt >= cutoff)
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, RECENT_LIMIT);
  }, [conversations]);

  const model = models.find((m) => m.id === activeModel);
  const host = typeof window !== "undefined" ? window.location.hostname || "local" : "local";

  const openCreate = () => {
    setEditor({ mode: "create" });
    setDraftLabel("");
    setDraftPrompt("");
    setDraftSkillIds([]);
  };

  const openEdit = (index: number) => {
    const t = topics[index];
    if (!t) return;
    setEditor({ mode: "edit", index, original: t });
    setDraftLabel(t.label);
    setDraftPrompt(t.prompt);
    setDraftSkillIds(t.skillIds ?? []);
  };

  const toggleDraftSkill = (id: string) => {
    setDraftSkillIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const closeEditor = () => setEditor(null);

  const saveEditor = async () => {
    const label = draftLabel.trim();
    const prompt = draftPrompt.trim();
    if (!label || !editor) return;
    if (!prompt && draftSkillIds.length === 0) return;
    const topic: HomeTopic = { label, prompt, skillIds: draftSkillIds };
    const next = [...topics];
    if (editor.mode === "create") next.push(topic);
    else next[editor.index] = topic;
    await updateSettings({ homeTopics: next });
    closeEditor();
  };

  const deleteEditor = async () => {
    if (!editor || editor.mode !== "edit") return;
    const next = topics.filter((_, i) => i !== editor.index);
    await updateSettings({ homeTopics: next });
    closeEditor();
  };

  return (
    <div className="w-full max-w-2xl rounded-2xl border bg-muted/30 p-6 shadow-sm">
      <div className="mb-5 flex items-center gap-2 text-xs text-muted-foreground">
        <i className="fa-solid fa-microchip" />
        <span className="font-mono">{activeModel || "no model"}</span>
        <span className="opacity-50">·</span>
        <span>{formatContextLength(model?.contextLength)} ctx</span>
        <span className="opacity-50">·</span>
        <span>{host}</span>
      </div>

      <div className="mb-5">
        <p className="mb-2 text-xs font-medium text-muted-foreground">Pick up where you left off</p>
        <div className="flex flex-wrap gap-2">
          {topics.map((t, i) => (
            <div key={`${t.label}-${i}`} className="group inline-flex items-center rounded-full border bg-background text-sm hover:border-primary">
              <button
                onClick={() => onPickTopic(t)}
                className="px-3 py-1.5 hover:text-primary"
                title={t.prompt}
              >
                {t.label}
              </button>
              <button
                onClick={() => openEdit(i)}
                className="rounded-full border-l px-2 py-1.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground"
                title="Edit"
              >
                <i className="fa-solid fa-pen-to-square text-[10px]" />
              </button>
            </div>
          ))}
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-1 rounded-full border border-dashed bg-background px-3 py-1.5 text-sm text-muted-foreground hover:border-primary hover:text-primary"
            title="Add a starter"
          >
            <i className="fa-solid fa-plus text-[10px]" /> New
          </button>
        </div>
      </div>

      {recents.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground">Recent</p>
          <ul className="flex flex-col gap-0.5">
            {recents.map((c) => (
              <li key={c.id}>
                <button
                  onClick={() => onSelectChat(c.id)}
                  className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
                >
                  <span className="truncate">{c.title}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{relativeTime(c.updatedAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {editor && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4"
          onClick={closeEditor}
        >
          <div
            className="w-full max-w-md rounded-lg border bg-background p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-sm font-semibold">
              {editor.mode === "create" ? "New starter" : "Edit starter"}
            </h2>
            <label className="mt-3 block text-xs font-medium text-muted-foreground">Title</label>
            <input
              autoFocus
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              placeholder="e.g. Daily standup"
              className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
            <label className="mt-3 block text-xs font-medium text-muted-foreground">
              Starter prompt <span className="opacity-70">(optional — leave blank to just preload skills)</span>
            </label>
            <textarea
              value={draftPrompt}
              onChange={(e) => setDraftPrompt(e.target.value)}
              placeholder="The first message that gets dropped into the chat input when you click this chip."
              rows={4}
              className="mt-1 w-full resize-y rounded-md border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
            {skills.length > 0 && (
              <>
                <label className="mt-3 block text-xs font-medium text-muted-foreground">
                  Skills <span className="opacity-70">(optional — auto-activated on the new chat)</span>
                </label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {skills.map((s) => {
                    const on = draftSkillIds.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleDraftSkill(s.id)}
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                          on
                            ? "border-primary bg-primary/15 text-primary"
                            : "bg-background text-muted-foreground hover:border-primary hover:text-foreground"
                        }`}
                      >
                        {on && <i className="fa-solid fa-check text-[9px]" />}
                        {s.name}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            <div className="mt-4 flex items-center justify-between gap-2">
              {editor.mode === "edit" ? (
                <button
                  onClick={deleteEditor}
                  className="rounded-md px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10"
                >
                  Delete
                </button>
              ) : (
                <span />
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={closeEditor}
                  className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={saveEditor}
                  disabled={!draftLabel.trim() || (!draftPrompt.trim() && draftSkillIds.length === 0)}
                  className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
